import type { ReactNode } from 'react'

import type { SelectOption, SearchableSelectValidationState } from '../searchable-select'

export interface MultiSelectProps<T = string> {
  /** Options to display */
  options: SelectOption<T>[]
  /** Selected values (array for multi-select) */
  value?: T[]
  /** Callback when values change */
  onChange?: (values: T[], selectedOptions: SelectOption<T>[]) => void
  /** Placeholder text */
  placeholder?: string
  /** Search placeholder text */
  searchPlaceholder?: string
  /** Validation state */
  validationState?: SearchableSelectValidationState
  /** Error message */
  error?: string
  /** Helper text */
  helperText?: string
  /** Size variant */
  size?: 'sm' | 'md' | 'lg'
  /** Hide the error/helper text area (when used inside FormField) */
  hideMessage?: boolean
  /** Allow clearing all selections */
  clearable?: boolean
  /** Custom option renderer */
  renderOption?: (option: SelectOption<T>, isSelected: boolean, isHighlighted: boolean) => ReactNode
  /** Custom chip renderer for selected items */
  renderChip?: (option: SelectOption<T>, onRemove: () => void) => ReactNode
  /** Filter function for search */
  filterOption?: (option: SelectOption<T>, searchQuery: string) => boolean
  /** No options text */
  noOptionsText?: string
  /** Loading state */
  isLoading?: boolean
  /** Maximum visible items (default: 8) */
  maxVisibleItems?: number
  /** Whether the select is disabled */
  disabled?: boolean
  /** ID for accessibility */
  id?: string
  /** Custom class name */
  className?: string
  /** Show as centered modal on desktop instead of attached dropdown */
  modal?: boolean
  /** Callback when dropdown open state changes */
  onOpenChange?: (isOpen: boolean) => void
  /** Maximum number of selections allowed */
  maxSelections?: number
  /** Show count of selected items */
  showCount?: boolean
}

export const sizeStyles = {
  sm: { padding: 'px-2.5 py-1.5', text: 'text-sm', minHeight: 'min-h-[34px]' },
  md: { padding: 'px-3 py-2', text: 'text-base', minHeight: 'min-h-[42px]' },
  lg: { padding: 'px-4 py-3', text: 'text-lg', minHeight: 'min-h-[50px]' },
}

export const validationStyles: Record<SearchableSelectValidationState, string> = {
  default: 'border-border focus:border-ring focus:ring-ring',
  valid: 'border-green-500 focus:border-green-500 focus:ring-green-500',
  invalid: 'border-red-500 focus:border-red-500 focus:ring-red-500',
}

export function defaultFilterOption<T>(option: SelectOption<T>, searchQuery: string): boolean {
  const query = searchQuery.toLowerCase()
  return (
    option.label.toLowerCase().includes(query) ||
    /* v8 ignore next */
    (option.description?.toLowerCase().includes(query) ?? false)
  )
}
